/**
 * src/api/appearance.ts
 * ----------------------
 * API function for fetching org-level appearance/branding configuration.
 *
 * Fully implemented this will:
 *   - getAppearance(orgId) — GET /appearance/{orgId} — returns an
 *     AppearanceConfig object containing:
 *       primaryColor, accentColor, logoUrl, faviconUrl,
 *       fontFamily, borderRadius, darkModeEnabled,
 *       welcomeMessage, agentAvatarUrl, orgName,
 *       allowAnonymous, voiceEnabled
 *   - The appearance store (src/stores/appearance.ts) calls this on mount
 *     and applies the config as CSS custom properties on <html>.
 *   - Response is cached aggressively (1 hour TTL via react-query) since
 *     branding rarely changes.
 *
 * Key dependencies:
 *   src/api/client.ts, src/stores/appearance.ts
 */
import apiClient from './client'

export interface AppearanceConfig {
  orgId: string
  orgName: string
  logoUrl: string | null
  faviconUrl: string | null
  agentAvatarUrl: string | null
  primaryColor: string         // e.g. "#3b82f6"
  accentColor: string          // e.g. "#8b5cf6"
  fontFamily: string           // e.g. "Inter"
  borderRadiusBubble: string   // e.g. "1.25rem"
  borderRadiusCard: string     // e.g. "0.75rem"
  darkModeEnabled: boolean
  welcomeMessage: string
  allowAnonymous: boolean
  voiceEnabled: boolean
  supportedLocales: string[]
  defaultLocale: string
}

/** Fetch branding/appearance config for a specific org. */
export async function getAppearance(orgId: string): Promise<AppearanceConfig> {
  const { data } = await apiClient.get<AppearanceConfig>(
    `/appearance/${orgId}`,
  )
  return data
}
